import {
  Box,
  Badge,
  Cards,
  Pagination,
  TextFilter,
  Header,
  Link,
} from '@amzn/awsui-components-core';
import {
  ContentRequest,
  ContentRequestState,
  ContentRequestType,
  UpdateContentRequestResponse,
  UserProfile,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';

import {
  getFilterCountOutOfTotalText,
  isBarRaiserForSuggestion,
  joinTagValues,
} from '../../api/UtilsApi';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import ContentRequestAssignmentActions from './ContentRequestAssignmentActions';
import ContentRequestStatus from './ContentRequestStatus';
import { ThumbUpButton, FeedbackRating } from '../feedback-service';
import { convertEntities } from '@amzn/awscat-editor';
import { AppContext, withAppContext } from '../../contexts/AppContext';
import { useCollection } from '@amzn/awsui-collection-hooks';

const DEFAULT_PAGE_SIZE = 10;
export const CARDS_PER_ROW = [{ cards: 1 }];

interface ContentRequestCardHeaderProps {
  contentRequest: ContentRequest;
  user: UserProfile;
  handleSuggestionVote: (requestId: string, rating: FeedbackRating) => Promise<void>;
}

interface ContentRequestCardBodyProps {
  contentRequest: ContentRequest;
  handleSuggestionUpdate: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId?: string,
  ) => Promise<UpdateContentRequestResponse>;
  user: UserProfile;
  isAdmin: boolean;
  isTechWriter: boolean;
  handleSuggestionDelete: (requestId: string) => Promise<void>;
}

interface ContentRequestListViewProps {
  contentRequests: ContentRequest[];
  isLoading: boolean;
  isAdmin: boolean;
  isTechWriter: boolean;
  filterSuggestions: (item: ContentRequest, filterText: string) => boolean;
  handleSuggestionUpdate: (
    contentRequestId: string,
    state: ContentRequestState,
    assigneeId?: string,
  ) => Promise<UpdateContentRequestResponse>;
  tableHeader: React.ReactNode;
  cardEmptyState: React.ReactNode;
  cardNoMatchState: React.ReactNode;
  user: UserProfile;
  handleSuggestionDelete: (requestId: string) => Promise<void>;
  handleSuggestionVote: (requestId: string, rating: FeedbackRating) => Promise<void>;
}

const ContentRequestListHeader = ({
  contentRequest,
  user,
  handleSuggestionVote,
}: ContentRequestCardHeaderProps) => {
  const appContext = useContext(AppContext);

  const renderThumbUpButton = () => {
    const { requestId, requestType } = contentRequest;
    if (requestType !== ContentRequestType.Update) {
      return (
        <>
          <ThumbUpButton
            resourceId={requestId}
            hasRated={contentRequest.hasRated ?? false}
            handleVote={handleSuggestionVote}
          />
          <Box color="text-body-secondary">{contentRequest.totalVotes}</Box>
        </>
      );
    } else {
      return null;
    }
  };

  const renderContentRequestHeader = () => {
    const {
      assigneeId,
      requestId,
      requestType,
      contentRequestData,
      userName,
      createdDate,
      state,
      contentId,
    } = contentRequest;
    const { contentName } = contentRequestData;
    const headerMessage =
      requestType === ContentRequestType.Update
        ? `Needs review as of `
        : `Suggested by ${userName} on `;
    const contentLinkAvailable =
      contentId && state === ContentRequestState.IN_PROGRESS && assigneeId === user.userId;
    const contentLink =
      requestType === ContentRequestType.Update
        ? getContentPath({ contentId: contentId ?? '', mode: 'publication' })
        : contentLinkAvailable
          ? getContentPath({ contentId: contentId ?? '', mode: 'draft', role: 'author' })
          : `/review-content-request/${requestId}`;
    return (
      <Header variant="h3">
        <div className="cr-list-title">
          <Link variant="secondary" href={contentLink} target="_blank">
            {convertEntities(contentName, appContext.entities)}
          </Link>
          {renderThumbUpButton()}
        </div>
        <Box color="text-body-secondary" fontSize="body-s">
          {`${headerMessage} ${createdDate.toLocaleString(DateTime.DATE_FULL)} | `}
          <Box variant="span" color="text-body-secondary" fontSize="body-s">
            <ContentRequestStatus
              contentRequestState={state}
              assigneeName={contentRequest.assigneeName}
            />
          </Box>
        </Box>
      </Header>
    );
  };

  return renderContentRequestHeader();
};

const ContentRequestListBody = ({
  contentRequest,
  handleSuggestionUpdate,
  user,
  isAdmin,
  isTechWriter,
  handleSuggestionDelete,
}: ContentRequestCardBodyProps) => {
  const [isBarRaiser, setIsBarRaiser] = useState<boolean>(false);

  const appContext = useContext(AppContext);

  useEffect(() => {
    setIsBarRaiser(isBarRaiserForSuggestion(user, contentRequest));
  }, [contentRequest, user]);

  const renderContentRequestDescription = () => {
    const { contentType, assignedDate, requestType } = contentRequest;
    const { tags, description, requireCode } = contentRequest.contentRequestData;
    const joinedTags = joinTagValues(tags);
    const tagsInfo = joinedTags.map((tag) => (
      <Box
        color="text-body-secondary"
        fontSize="body-s"
        padding={{ top: 's' }}
        key={tag.key + tag.value}
      >
        {tag.key}: {tag.value}
      </Box>
    ));
    const assignmentDate = assignedDate ? (
      <Box
        color="text-body-secondary"
        padding={{ top: 's' }}
      >{`Assigned on: ${assignedDate.toLocaleString(DateTime.DATE_FULL)}`}</Box>
    ) : null;
    const requireCodeMessage =
      requestType === ContentRequestType.Update ? 'Contains code' : 'Requires code';
    const outdatedContentBadge =
      requestType === ContentRequestType.Update ? (
        <Badge color="blue">Potentially outdated content</Badge>
      ) : null;

    return (
      <>
        <div className="cr-list-view-desc">
          {convertEntities(description, appContext.entities)}
          <p className="cr-list-view-misc">
            {outdatedContentBadge}
            &nbsp;
            {requireCode ? <Badge color="grey">{requireCodeMessage}</Badge> : null}
          </p>
        </div>
        <Box fontSize="body-s" padding={{ horizontal: 'm', bottom: 'm' }}>
          <Box variant="span" color="text-body-secondary">{`Content type: ${contentType}`}</Box>
          {tagsInfo}
          {assignmentDate}
        </Box>
      </>
    );
  };

  return (
    <div className="cr-list-view-wrapper">
      <div className="cr-list-view-detail">
        <div className="cr-list-view-desc-container">{renderContentRequestDescription()}</div>
      </div>
      <ContentRequestAssignmentActions
        contentRequest={contentRequest}
        handleSuggestionUpdate={handleSuggestionUpdate}
        userId={user.userId}
        isAdmin={isAdmin}
        isBarRaiser={isBarRaiser}
        isTechWriter={isTechWriter}
        isVerticalView
        handleSuggestionDelete={handleSuggestionDelete}
      />
    </div>
  );
};

const ContentRequestListView = ({
  contentRequests,
  isLoading,
  tableHeader,
  cardEmptyState,
  cardNoMatchState,
  filterSuggestions,
  handleSuggestionUpdate,
  isAdmin,
  isTechWriter,
  user,
  handleSuggestionDelete,
  handleSuggestionVote,
}: ContentRequestListViewProps) => {
  const cardDefinition = {
    header: (item: ContentRequest) => (
      <ContentRequestListHeader
        contentRequest={item}
        user={user}
        handleSuggestionVote={handleSuggestionVote}
      />
    ),
    sections: [
      {
        // @ts-expect-error TS7006 - strict migration
        content: (item) => (
          <ContentRequestListBody
            contentRequest={item}
            user={user}
            handleSuggestionUpdate={handleSuggestionUpdate}
            isTechWriter={isTechWriter}
            isAdmin={isAdmin}
            handleSuggestionDelete={handleSuggestionDelete}
          />
        ),
        id: 'body',
      },
    ],
  };

  const getFilterCounterText = (count: number) => {
    return getFilterCountOutOfTotalText(count, contentRequests.length);
  };

  const { items, paginationProps, collectionProps, filterProps } = useCollection(contentRequests, {
    filtering: {
      filteringFunction: filterSuggestions,
      defaultFilteringText: '',
      noMatch: cardNoMatchState,
      empty: cardEmptyState,
    },
    pagination: {
      pageSize: DEFAULT_PAGE_SIZE,
    },
  });

  return (
    <Cards
      {...collectionProps}
      cardDefinition={cardDefinition}
      header={tableHeader}
      items={items}
      cardsPerRow={CARDS_PER_ROW}
      loadingText={constants.CONTENT_REQUEST_LOADING_SUGGESTION_LABEL}
      loading={isLoading}
      pagination={
        <Pagination
          {...paginationProps}
          ariaLabels={{
            nextPageLabel: 'Next page',
            previousPageLabel: 'Previous page',
            pageLabel: (pageNumber) => `Page ${pageNumber}`,
          }}
        />
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder={constants.CONTENT_REQUEST_SEARCH_SUGGESTION_LABEL}
          filteringAriaLabel="Filter content requests"
          countText={getFilterCounterText(items.length)}
        />
      }
    />
  );
};

export default withAppContext(ContentRequestListView);
